import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Avatar,
} from '@mui/material';
import { Phone, Search, Build, Email } from '@mui/icons-material';
import ProgressIndicator from './ProgressIndicator';

interface DiagnosticFormProps {
  onStartDiagnostics: (data: { yourEmail: string; yourNumber: string; destinationNumber: string }) => void;
}

const DiagnosticForm: React.FC<DiagnosticFormProps> = ({ onStartDiagnostics }) => {
  const [formData, setFormData] = useState({
    yourEmail: '',
    yourNumber: '',
    destinationNumber: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.yourEmail.trim()) {
      newErrors.yourEmail = 'Your email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.yourEmail)) {
      newErrors.yourEmail = 'Please enter a valid email address';
    }

    if (!formData.yourNumber.trim()) {
      newErrors.yourNumber = 'Your phone number is required';
    } else if (!/^\d{10,11}$/.test(formData.yourNumber.replace(/\D/g, ''))) {
      newErrors.yourNumber = 'Please enter a valid 10-digit phone number';
    }

    // Destination number is now optional - only validate if provided
    if (formData.destinationNumber.trim()) {
      if (!/^\d{10,11}$/.test(formData.destinationNumber.replace(/\D/g, ''))) {
        newErrors.destinationNumber = 'Please enter a valid 10-digit phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Normalize phone numbers to +1XXXXXXXXXX format for API calls
      const normalizedData = {
        yourEmail: formData.yourEmail.trim(),
        yourNumber: normalizePhoneNumber(formData.yourNumber),
        destinationNumber: formData.destinationNumber.trim() ? normalizePhoneNumber(formData.destinationNumber) : '',
      };
      onStartDiagnostics(normalizedData);
    }
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) {
      return '';
    } else if (cleaned.length <= 10) {
      // Format as (XXX) XXX-XXXX for display
      if (cleaned.length <= 3) {
        return cleaned;
      } else if (cleaned.length <= 6) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    } else {
      // For numbers longer than 10 digits, assume it includes country code
      return cleaned;
    }
  };

  const normalizePhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 10) {
      // Add +1 prefix for 10-digit US numbers
      return `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // Already has country code, add + prefix
      return `+${cleaned}`;
    } else if (cleaned.length > 11) {
      // Already has country code, add + prefix
      return `+${cleaned}`;
    }
    // For 10-digit numbers, add +1 prefix
    return `+1${cleaned}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: { xs: 2, md: 4 },
      }}
    >
      <ProgressIndicator currentStep={1} steps={['Input', 'Diagnostics', 'Results', 'Report']} />
      
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          padding: { xs: 3, md: 4 },
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
          borderRadius: 3,
          backgroundColor: 'white',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 5, mt: -4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: 'primary.main',
                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
              }}
            >
              <Build sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Messaging Diagnostic Tool
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ 
              mb: 3,
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            Let's troubleshoot your messaging issue together
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
                fontSize: '1rem',
              }}
            >
              Your email *
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="your.email@example.com"
              value={formData.yourEmail}
              onChange={(e) => handleInputChange('yourEmail', e.target.value)}
              error={!!errors.yourEmail}
              helperText={errors.yourEmail}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'grey.500' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 14px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
                fontSize: '1rem',
              }}
            >
              Your phone number *
            </Typography>
            <TextField
              fullWidth
              placeholder="(122) 233-3444"
              value={formData.yourNumber}
              onChange={(e) => handleInputChange('yourNumber', formatPhoneNumber(e.target.value))}
              error={!!errors.yourNumber}
              helperText={errors.yourNumber}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: 'grey.500' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 14px',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
                fontSize: '1rem',
              }}
            >
              Destination number (having issues with)
            </Typography>
            <TextField
              fullWidth
              placeholder="(155) 566-6777"
              value={formData.destinationNumber}
              onChange={(e) => handleInputChange('destinationNumber', formatPhoneNumber(e.target.value))}
              error={!!errors.destinationNumber}
              helperText={errors.destinationNumber}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: 'grey.500' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '16px 14px',
                },
              }}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Search />}
            sx={{
              borderRadius: 3,
              py: 2,
              px: 4,
              fontWeight: 700,
              fontSize: '1.1rem',
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Start Diagnostics
          </Button>
        </form>
      </Card>
    </Box>
  );
};

export default DiagnosticForm; 
